import React, { useLayoutEffect } from "react";
import "./DashboardStatsNew.scss";
import PageWrapper from "../../ui/layout/PageWrapper";
import StatsBar from "../../ui/stats-bar/StatsBar";
import StatsPanel from "../../ui/stats-panel/StatsPanel";
import TabbedContainer from "../../ui/tabbed-container/TabbedContainer";
import { NavLink, Redirect, Switch } from "react-router-dom";
import { DashboardChartType } from "../../../models/Dashboard";
import counterpart from "counterpart";
import { PrivateRoute } from "../../../containers/AuthRoutes";
import DashboardChartSection from "../dashboard-chart-section/DashboardChartSection";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../..";
import { IDashboardReducer } from "../../../reducers/dashboardReducer";
import { IAuthReducerState } from "../../../reducers/authReducer";
import { fetchDashboardRecentData, fetchDashboardNewCurrentData } from "../../../actions/dashboardActions";
import history from "../../../utils/history";
import { setInvoicesField } from "../../../actions/invoicesActions";
import { InvoiceStatus } from "../../../models/Invoices";
import { setCustomersField } from "../../../actions/customersActions";
import { CardStatusType, CustomerStatusType } from "../../../models/Customers";


const DashboardStatsNew: React.FC = () => {
  const { recentData, newCurrentData } = useSelector<AppState, IDashboardReducer>(state => state.dashboardReducer)
  const { dashboardSettings, currentCompanyDomain, currentCompanyId } = useSelector<AppState, IAuthReducerState>(state => state.auth)
  const dispatch = useDispatch<Function>()

  useLayoutEffect(() => {
    dispatch(fetchDashboardRecentData(currentCompanyDomain));
    dispatch(fetchDashboardNewCurrentData(Number(currentCompanyId)));
  }, [dashboardSettings]);

  if (!recentData || !newCurrentData) {
    return null;
  }

  const { signUps, customers, revenueToday, revenueMonth } = recentData;

  const getStatPanelType = (percentageChanged: number, hasValueDropped: boolean): "positive" | "negative" | "equals" => {
    if (!percentageChanged) {
      return "equals";
    }
    if (hasValueDropped) {
      return "negative";
    }
    return "positive";
  }

  return (
    <PageWrapper className="dashboard-stats">
      <StatsBar
        data={[
          {
            label: "DASHBOARD_STATS_BAR_MRR",
            value: newCurrentData.getMrrFormatted(),
            infoText: "DASHBOARD_STATS_NEW_BAR_MRR_INFO"
          },
          {
            label: "DASHBOARD_STATS_BAR_SUBSCRIPTIONS",
            value: newCurrentData.getActiveSubscriptionsCount(),
            infoText: "DASHBOARD_STATS_BAR_SUBSCRIPTIONS_INFO"
          },
          {
            label: "DASHBOARD_NEW_STATS_BAR_NET_SALES_MTD",
            value: newCurrentData.getNetSalesMtdFormatted(),
            infoText: "DASHBOARD_STATS_BAR_NET_REVENUE_MTD_INFO"
          },
          {
            label: "DASHBOARD_NEW_STATS_BAR_NET_REVENUE_MTD",
            value: newCurrentData.getNetRevenueMtdFormatted(),
            infoText: "DASHBOARD_STATS_BAR_NET_PAYMENTS_MTD_INFO"
          },
          {
            label: "DASHBOARD_STATS_BAR_UNPAID_INVOICES",
            value: newCurrentData.getUnpaidInvoicesCount(),
            value2: newCurrentData.getUnpaidInvoicesFormatted(),
            infoText: "DASHBOARD_STATS_BAR_UNPAID_INVOICES_INFO",
            clb: () => { 
              history.push({
                pathname: "/invoices",
                state: { 
                    from: "/new-home"
                }
              })
              dispatch(setInvoicesField("invoiceStatus", { label: "INVOICE_STATUS_FILTER_UNPAID", value: InvoiceStatus.UNPAID }));
            }
          },
          {
            label: "DASHBOARD_STATS_BAR_EXPIRED_CARDS",
            value: newCurrentData.getExpiredCardsCount(),
            infoText: "DASHBOARD_STATS_BAR_EXPIRED_CARDS_INFO",
            clb: () => {
              history.push({
                pathname: "/customers",
                state: { 
                    from: "/new-home"
                }
              })
              dispatch(setCustomersField("customerStatus", { label: "CUSTOMERS_LIST_TABLE_ACTIVE", value: CustomerStatusType.ACTIVE }));
              dispatch(setCustomersField("cardStatus", { label: "CUSTOMERS_LIST_TABLE_EXPIRED_CARD", value: CardStatusType.EXPIRED }));
            }
          }
        ]}
      />
      <StatsPanel title="DASHBOARD_STATS_PANEL_REVENUE_THIS_MONTH" percentage={revenueMonth.percentageChanged || 0} type={getStatPanelType(revenueMonth.percentageChanged || 0, revenueMonth.hasValueDropped)} infoText="DASHBOARD_STATS_PANEL_REVENUE_THIS_MONTH_INFO">
        <StatsPanel.VALUES
          value1={revenueMonth.expectedValueFormatted}
          label1="DASHBOARD_STATS_PANEL_EXPECTED"
          value2={revenueMonth.lastMonthsValueFormatted ? revenueMonth.lastMonthsValueFormatted : ""}
          label2="DASHBOARD_STATS_PANEL_LAST_MONTH"
        />
        <StatsPanel.VALUES
          value1={revenueMonth.todaysValueFormatted}
          label1="DASHBOARD_STATS_PANEL_RECEIVED"
        />
      </StatsPanel>
      <StatsPanel title="DASHBOARD_STATS_PANEL_REVENUE_TODAY" percentage={revenueToday.percentageChanged || 0} type={getStatPanelType(revenueToday.percentageChanged || 0, revenueToday.hasValueDropped)} infoText="DASHBOARD_STATS_PANEL_REVENUE_TODAY_INFO">
        <StatsPanel.VALUES
          value1={revenueToday.expectedValueFormatted}
          label1="DASHBOARD_STATS_PANEL_EXPECTED"
          value2={revenueToday.lastMonthsValueFormatted ? revenueToday.lastMonthsValueFormatted : ""}
          label2="DASHBOARD_STATS_PANEL_SAME_DAY_LAST_MONTH"
        />
        <StatsPanel.VALUES
          value1={revenueToday.todaysValueFormatted}
          label1="DASHBOARD_STATS_PANEL_RECEIVED"
        />
      </StatsPanel>
      <StatsPanel title="DASHBOARD_STATS_PANEL_CUSTOMER_BASE" percentage={customers.percentageChanged || 0} type={getStatPanelType(customers.percentageChanged || 0, customers.hasValueDropped)} infoText="DASHBOARD_STATS_PANEL_CUSTOMER_BASE_INFO" percentageText="DASHBOARD_STATS_PANEL_REVENUE_THIS_MONTH_PERCENTAGE">
        <StatsPanel.VALUES
          value1={customers.todaysValue + ""}
          label1="DASHBOARD_STATS_PANEL_TODAY"
          value2={customers.lastMonthsValue ? customers.lastMonthsValue + "" : ""}
          label2="DASHBOARD_STATS_PANEL_ONE_MONTH_AGO"
        />
      </StatsPanel>
      <StatsPanel title="DASHBOARD_STATS_PANEL_SIGNUPS" percentage={signUps.percentageChanged || 0} type={getStatPanelType(signUps.percentageChanged || 0, signUps.hasValueDropped)} infoText="DASHBOARD_STATS_PANEL_SIGNUPS_INFO" percentageText="DASHBOARD_STATS_PANEL_SIGNUPS_PERCENTAGE">
        <StatsPanel.VALUES
          value1={signUps.todaysValue + ""}
          label1="DASHBOARD_STATS_PANEL_SO_FAR_THIS_MONTH"
          value2={signUps.lastMonthsValue ? signUps.lastMonthsValue + "" : ""}
          label2="DASHBOARD_STATS_PANEL_AT_THIS_TIME_LAST_MONTH"
        />
      </StatsPanel>

      <TabbedContainer type="submenu">
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.SALES}`}>{counterpart("DASHBOARD_STATS_MENU_SALES")}</NavLink>
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.PAYMENTS}`}>{counterpart("DASHBOARD_STATS_MENU_PAYMENTS")}</NavLink>
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.REFUNDS}`}>{counterpart("DASHBOARD_STATS_MENU_REFUNDS")}</NavLink>
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.MRR}`}>{counterpart("DASHBOARD_STATS_MENU_MRR")}</NavLink>
        {/* NOT IMPLEMENTED FOR NOW
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.CMRR}`}>{counterpart('DASHBOARD_STATS_MENU_CMRR')}</NavLink>*/}
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.SIGNUPS}`}>{counterpart("DASHBOARD_STATS_MENU_SIGNUPS")}</NavLink>
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.CHURN}`}>{counterpart("DASHBOARD_STATS_MENU_CHURN")}</NavLink>
        <NavLink activeClassName="active" to={`/new-home/${DashboardChartType.CREDITS}`}>{counterpart("DASHBOARD_STATS_MENU_CREDITS")}</NavLink>
      </TabbedContainer>

      <Switch>
        <PrivateRoute path={`/new-home/${DashboardChartType.SALES}`} render={() => <DashboardChartSection type={DashboardChartType.SALES} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.PAYMENTS}`} render={() => <DashboardChartSection type={DashboardChartType.PAYMENTS} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.REFUNDS}`} render={() => <DashboardChartSection type={DashboardChartType.REFUNDS} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.MRR}`} render={() => <DashboardChartSection type={DashboardChartType.MRR} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.CMRR}`} render={() => <DashboardChartSection type={DashboardChartType.CMRR} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.SIGNUPS}`} render={() => <DashboardChartSection type={DashboardChartType.SIGNUPS} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.CHURN}`} render={() => <DashboardChartSection type={DashboardChartType.CHURN} />} />
        <PrivateRoute path={`/new-home/${DashboardChartType.CREDITS}`} render={() => <DashboardChartSection type={DashboardChartType.CREDITS} />} />
        <Redirect exact from="/new-home" to={`/new-home/${DashboardChartType.SALES}`} />
      </Switch>
    </PageWrapper>
  )
}

export default DashboardStatsNew